import React from 'react';
import { TextField, Typography, styled } from '@mui/material';
import PropTypes from 'prop-types';
import ExclamationIcon from '../../../Component/Icons/ExclamationIcon';
import { NODE_UI } from '../../../NodeUI/nodeUiStyles';

const getAdornment = (error, value) => (error && value === '' ? <ExclamationIcon /> : null);

const EMPTY_FIELD_ERROR_MESSAGE = 'This field cannot be empty';

const getHelperText = (error, value, errorMessage) =>
  error ? (value === '' ? EMPTY_FIELD_ERROR_MESSAGE : errorMessage || null) : null;

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: NODE_UI.fontSize.value,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.text.primary,
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
  },
  '& .MuiInputBase-multiline': { padding: 0 },
  '& .MuiInputBase-inputMultiline': { padding: NODE_UI.input.padding },
  '& .MuiFormHelperText-root': {
    fontSize: NODE_UI.fontSize.caption,
    marginLeft: 0,
    marginTop: '4px',
  },
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '6px',
}));

const CredentialTextField = ({
  label,
  placeholder,
  description,
  value,
  error,
  errorMessage,
  onChange,
  minRows,
  multiline,
  type = 'text',
  allowEmpty = true,
  ...props
}) => (
  <>
    {typeof label === 'string' ? <Label>{label}</Label> : label}
    {description && <Description>{description}</Description>}
    <StyledTextField
      size="small"
      fullWidth
      multiline={multiline}
      minRows={minRows}
      value={value}
      variant="outlined"
      placeholder={placeholder}
      onChange={onChange}
      type={type}
      error={(value === '' && !allowEmpty) || error}
      InputProps={{
        endAdornment: getAdornment(error, value),
      }}
      helperText={getHelperText(error, value, errorMessage)}
      {...props}
    />
  </>
);

CredentialTextField.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  placeholder: PropTypes.string,
  description: PropTypes.string,
  value: PropTypes.string.isRequired,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  minRows: PropTypes.number,
  multiline: PropTypes.bool,
  type: PropTypes.string,
  allowEmpty: PropTypes.bool,
};

export { getAdornment, getHelperText, CredentialTextField };
